import { Injectable } from "@angular/core";
import {
  Actions,
  createEffect,
  EffectNotification,
  ofType,
  OnRunEffects
} from "@ngrx/effects";
import { AccountCamMicStreamService } from "@youm-front/account-cammic/data-access";
import { LocalConferenceStreamsListService } from "@youm-front/conference-stream/data-access";
import { Observable } from "rxjs";
import {
  concatMap,
  distinctUntilChanged,
  exhaustMap,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap
} from "rxjs/operators";
import { LocalCamMicConferenceStream } from "youm-lib/conference";
import { RoomActions, RoomProducerActions } from "../actions";
import { LocalCamMicStreamService } from "../services";

@Injectable()
export class RoomProducerCamMicEffects implements OnRunEffects {
  constructor(
    private readonly _actions$: Actions,
    private readonly _accountCamMicStreamService: AccountCamMicStreamService,
    private readonly _localCamMicStreamService: LocalCamMicStreamService,
    private readonly localConferenceStreamsListService: LocalConferenceStreamsListService
  ) {}

  updateUserCamMicStream$ = createEffect(
    () =>
      this.localConferenceStreamsListService.streams$.pipe(
        map((_) => _.find((_) => _.isCamMic)),
        mergeMap((_) => (_ ? [_ as LocalCamMicConferenceStream] : [])),
        distinctUntilChanged(),
        switchMap((_) =>
          this._accountCamMicStreamService.streams$.pipe(
            tap(([stream0, stream1]) => {
              if (stream1) {
                _.update(stream1);
              }

              if (stream0) {
                stream0.destroy();
              }
            })
          )
        )
      ),
    { dispatch: false }
  );

  toggleLocalVideoStream$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomActions.toggleLocalVideoStream),
      map(() => this.camMicStreamGet()),
      mergeMap((_) => (_ ? [_] : [])),
      mergeMap((_) => _.isVideoEnabled$.pipe(take(1))),
      mergeMap((_) =>
        _
          ? [RoomActions.unpicLocalWebcamStream()]
          : [RoomActions.picLocalWebcamStream()]
      )
    )
  );

  toggleLocalAudioStream$ = createEffect(() =>
    this._actions$.pipe(
      ofType(RoomActions.toggleLocalAudioStream),
      map(() => this.camMicStreamGet()),
      mergeMap((_) => (_ ? [_] : [])),
      mergeMap((_) => _.isAudioEnabled$.pipe(take(1))),
      mergeMap((_) =>
        _
          ? [RoomActions.muteLocalWebcamStream()]
          : [RoomActions.unmuteLocalWebcamStream()]
      )
    )
  );

  picLocalWebcamStream$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomActions.picLocalWebcamStream),
        concatMap(() => this._localCamMicStreamService.videoEnable())
      ),
    { dispatch: false }
  );

  unpicLocalWebcamStream$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomActions.unpicLocalWebcamStream),
        concatMap(() => this._localCamMicStreamService.videoDisable())
      ),
    { dispatch: false }
  );

  muteLocalWebCamStream$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomActions.muteLocalWebcamStream),
        concatMap(() => this._localCamMicStreamService.audioDisable())
      ),
    { dispatch: false }
  );

  unmuteLocalWebCamStream$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomActions.unmuteLocalWebcamStream),
        concatMap(() => this._localCamMicStreamService.audioEnable())
      ),
    { dispatch: false }
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this._actions$.pipe(
      ofType(RoomProducerActions.init),
      exhaustMap(() =>
        resolvedEffects$.pipe(
          takeUntil(this._actions$.pipe(ofType(RoomProducerActions.destroy)))
        )
      )
    );
  }

  private camMicStreamGet() {
    const ls = this.localConferenceStreamsListService.streams.find(
      (_) => _.isCamMic
    );
    if (!ls) {
      return;
    }

    return ls as LocalCamMicConferenceStream;
  }
}
